import { useState } from 'react';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ActivityIndicator from 'components/ActivityIndicatorComponent';
import { useGetCubeDataEntryAudit } from 'services/v1/Tenant/AROCube/CubeDataEntryService';
import {
  CubeAuditMismatch,
  CubeAuditMismatchType,
  ResponseCubeDataEntryAudit,
} from 'types/api/Tenant/AROCube/CubeDataEntryTypes';
import { getErrorMessage } from 'utils/Error';

interface ModalDeveloperToolsAuditProps {
  open: boolean;
  onClose: () => void;
  cubeId: string | undefined;
  layerId: string | undefined;
  layerName?: string;
}

const modalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90vw',
  maxWidth: 1100,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: 1,
  px: 3,
  py: 2,
  outline: 'none',
  display: 'flex',
  flexDirection: 'column' as const,
};

const MISMATCH_TYPE_COLOR: Record<string, { bg: string; fg: string }> = {
  VALUE_MISMATCH: { bg: '#FDECEA', fg: '#B71C1C' },
  MISSING_PHYSICAL_ROW: { bg: '#FFF4E5', fg: '#8A5300' },
  INVALID_PHYSICAL_ROW: { bg: '#EDE7F6', fg: '#4A148C' },
};

function getMismatchColor(type: CubeAuditMismatchType) {
  return MISMATCH_TYPE_COLOR[type] || { bg: '#ECEFF1', fg: '#37474F' };
}

function formatValue(value: number | string | null) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return String(value);
  return value;
}

function SummaryStat({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Tooltip title={hint || ''} arrow placement='top' disableHoverListener={!hint}>
      <Box
        sx={{
          flex: 1,
          minWidth: 110,
          px: 1.5,
          py: 1,
          border: '1px solid #E3EBF6',
          borderRadius: 1,
          bgcolor: '#FAFBFD',
        }}
      >
        <Typography variant='caption' sx={{ color: '#888', display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10 }}>
          {label}
        </Typography>
        <Typography variant='body1' sx={{ color: '#3B4797', fontWeight: 600, mt: 0.25 }}>
          {value}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function HealthBadge({ healthy }: { healthy: boolean }) {
  return (
    <Chip
      size='small'
      icon={healthy ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
      label={healthy ? 'Healthy' : 'Unhealthy'}
      sx={{
        bgcolor: healthy ? '#E6F4EA' : '#FDECEA',
        color: healthy ? '#1B5E20' : '#B71C1C',
        fontWeight: 600,
        '& .MuiChip-icon': { color: healthy ? '#1B5E20' : '#B71C1C' },
      }}
    />
  );
}

function MismatchTable({ mismatches }: { mismatches: CubeAuditMismatch[] }) {
  if (mismatches.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#FAFBFD', borderRadius: 1, border: '1px dashed #E3EBF6' }}>
        <Typography variant='body2' color='text.secondary'>
          No mismatches reported.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper variant='outlined' sx={{ overflow: 'hidden' }}>
      <Box sx={{ overflow: 'auto', maxHeight: '40vh' }}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }}>Member</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }}>X-Member</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }}>Time Period</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }}>Attribute</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }} align='right'>Physical</TableCell>
              <TableCell sx={{ fontWeight: 700, bgcolor: '#F4F6FA' }} align='right'>Calculated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mismatches.map((m, idx) => {
              const color = getMismatchColor(m.type);
              return (
                <TableRow key={`${m.type}-${m.memberId ?? 'null'}-${m.timestamp ?? 'null'}-${m.attribute ?? 'null'}-${idx}`} hover>
                  <TableCell>
                    <Chip
                      size='small'
                      label={m.type}
                      sx={{ bgcolor: color.bg, color: color.fg, fontWeight: 600, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{m.memberId ?? '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{m.xMemberId ?? '—'}</TableCell>
                  <TableCell>{m.timePeriod ?? '—'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{m.attribute ?? '—'}</TableCell>
                  <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatValue(m.physicalValue)}</TableCell>
                  <TableCell align='right' sx={{ fontFamily: 'monospace', fontSize: 12 }}>{formatValue(m.calculatedValue)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}

function RawJsonView({ data }: { data: ResponseCubeDataEntryAudit }) {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(data, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore clipboard failures
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title={copied ? 'Copied!' : 'Copy JSON'} arrow placement='left'>
        <IconButton
          size='small'
          onClick={handleCopy}
          sx={{ position: 'absolute', top: 6, right: 6, bgcolor: '#fff', '&:hover': { bgcolor: '#f0f0f0' } }}
        >
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Box
        component='pre'
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: '#0F172A',
          color: '#E2E8F0',
          fontSize: 11,
          fontFamily: 'monospace',
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: '30vh',
        }}
      >
        {json}
      </Box>
    </Box>
  );
}

export function ModalDeveloperToolsAudit({
  open,
  onClose,
  cubeId,
  layerId,
  layerName,
}: ModalDeveloperToolsAuditProps) {
  const [showRaw, setShowRaw] = useState(false);

  const {
    data: auditResponse,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetCubeDataEntryAudit(cubeId, layerId, { enabled: open });

  const audit = auditResponse?.data;

  const handleClose = () => {
    setShowRaw(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Stack sx={modalContentStyle} spacing={2}>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Stack direction='row' spacing={1.5} alignItems='center'>
            <Typography variant='body1' fontWeight='bold' sx={{ color: '#3B4797' }}>
              Developer Tools — Cube Audit
            </Typography>
            {audit && <HealthBadge healthy={audit.healthy} />}
          </Stack>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Tooltip title='Refresh' arrow>
              <span>
                <IconButton size='small' onClick={() => refetch()} disabled={isFetching || !cubeId || !layerId}>
                  <RefreshIcon sx={{ color: '#3B4797' }} />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size='small' onClick={handleClose}>
              <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
            </IconButton>
          </Stack>
        </Stack>

        <Stack direction='row' spacing={2} alignItems='center' flexWrap='wrap'>
          <Typography variant='caption' color='text.secondary'>
            <b>Cube:</b> <span style={{ fontFamily: 'monospace' }}>{cubeId ?? '—'}</span>
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            <b>Layer:</b>{' '}
            <span style={{ fontFamily: 'monospace' }}>
              {layerName ? `${layerName} (${layerId ?? '—'})` : (layerId ?? '—')}
            </span>
          </Typography>
          {audit?.tableName && (
            <Typography variant='caption' color='text.secondary'>
              <b>Table:</b> <span style={{ fontFamily: 'monospace' }}>{audit.tableName}</span>
            </Typography>
          )}
        </Stack>

        <Divider />

        <Box sx={{ flex: 1, overflow: 'auto', pr: 0.5 }}>
          {!cubeId || !layerId ? (
            <Alert severity='warning'>Select a cube and layer to run the audit.</Alert>
          ) : isFetching && !audit ? (
            <Stack alignItems='center' justifyContent='center' sx={{ py: 4 }}>
              <ActivityIndicator />
              <Typography variant='caption' color='text.secondary' sx={{ mt: 1 }}>
                Running audit…
              </Typography>
            </Stack>
          ) : isError ? (
            <Alert severity='error'>{getErrorMessage(error)}</Alert>
          ) : audit ? (
            <Stack spacing={2}>
              <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                <SummaryStat label='Physical Rows' value={audit.physicalRowCount} hint='Rows currently stored in the physical table' />
                <SummaryStat label='Calculated Rows' value={audit.calculatedRowCount} hint='Rows the engine expected to produce' />
                <SummaryStat label='Checked Rows' value={audit.checkedRowCount} hint='Rows compared during this audit' />
                <SummaryStat label='Checked Cells' value={audit.checkedCellCount} hint='Individual cells compared during this audit' />
                <SummaryStat label='Mismatches' value={audit.mismatchCount} hint='Total mismatches detected' />
                <SummaryStat label='Missing Rows' value={audit.missingPhysicalRowCount} hint='Calculated rows missing from physical storage' />
                <SummaryStat label='Tolerance' value={audit.tolerance} hint='Numeric tolerance applied when comparing values' />
                <SummaryStat label='Limit' value={audit.limit} hint='Maximum mismatches returned (truncated if reached)' />
              </Stack>

              {audit.truncated && (
                <Alert severity='info'>
                  Results were truncated at {audit.limit} mismatches — there may be more issues not shown below.
                </Alert>
              )}

              <Stack direction='row' alignItems='center' justifyContent='space-between'>
                <Typography variant='body2' fontWeight={600} sx={{ color: '#3B4797' }}>
                  Mismatches ({audit.mismatches.length})
                </Typography>
                <Button
                  size='small'
                  variant='text'
                  onClick={() => setShowRaw((v) => !v)}
                  sx={{ textTransform: 'none' }}
                >
                  {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
                </Button>
              </Stack>

              <MismatchTable mismatches={audit.mismatches} />

              {showRaw && <RawJsonView data={audit} />}
            </Stack>
          ) : null}
        </Box>

        <Divider />
        <Stack direction='row' spacing={1.5} justifyContent='flex-end'>
          <Button
            variant='main-table-panel-border'
            onClick={handleClose}
            sx={{ textTransform: 'none' }}
          >
            Close
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
}
